import React, { useRef, useEffect, useCallback, useState } from 'react';
import {
  CarouselContainer,
  CarouselSlide,
  SlidesWrapper,
  DotsContainer,
  Dot,
} from './styles';

const CARD_WIDTH = 320;
const CARD_GAP = 24;
const CARD_TOTAL_WIDTH = CARD_WIDTH + CARD_GAP;
const VISIBLE_CARDS = 3; // amount of cards per page

interface Props {
  children: React.ReactNode;
  speed?: number;
}

const Carousel: React.FC<Props> = ({ children, speed = 0.8 }) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);

  const offsetRef = useRef(0);
  const dragOffsetRef = useRef(0);
  const isDraggingRef = useRef(false);
  const isPausedRef = useRef(false);
  const startXRef = useRef(0);

  const [activeDot, setActiveDot] = useState(0);

  const slides = React.Children.toArray(children);
  const repeated = [...slides, ...slides];
  const singleSetWidth = slides.length * CARD_TOTAL_WIDTH - CARD_GAP;
  const wrapperWidth = repeated.length * CARD_TOTAL_WIDTH - CARD_GAP;

  const totalDots = Math.ceil(slides.length / VISIBLE_CARDS);
  const isStatic = slides.length <= 2; // disabled loop and drag if there's one or two cards

  const applyTransform = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const current =
      offsetRef.current + (isDraggingRef.current ? dragOffsetRef.current : 0);
    el.style.transform = `translate3d(${current}px, 0, 0)`;
  }, []);

  const animate = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;

    if (!isPausedRef.current && !isDraggingRef.current) {
      offsetRef.current -= speed;
      if (Math.abs(offsetRef.current) >= singleSetWidth) {
        offsetRef.current += singleSetWidth;
      }
    }

    // update active dot
    const rawOffset = Math.abs(offsetRef.current % singleSetWidth);
    const activePage =
      Math.floor(rawOffset / (CARD_TOTAL_WIDTH * VISIBLE_CARDS)) % totalDots;
    setActiveDot(activePage);

    applyTransform();
    rafRef.current = requestAnimationFrame(animate);
  }, [applyTransform, speed, singleSetWidth, totalDots]);

  useEffect(() => {
    if (isStatic) return; // doesn't run loop if there's little slides
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [animate, isStatic]);

  // Handlers
  const onPointerDown = (e: React.PointerEvent) => {
    if (isStatic) return; // avoid drag when there's 1 or 2 cards
    const el = wrapperRef.current;
    if (!el) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    isDraggingRef.current = true;
    isPausedRef.current = true;
    dragOffsetRef.current = 0;
    startXRef.current = e.clientX;
    el.style.cursor = 'grabbing';
  };

  const onPointerMoveWindow = (e: PointerEvent) => {
    if (!isDraggingRef.current) return;
    dragOffsetRef.current = e.clientX - startXRef.current;
    applyTransform();
  };

  const onPointerUpWindow = () => {
    if (!isDraggingRef.current) return;
    offsetRef.current += dragOffsetRef.current;
    while (offsetRef.current <= -singleSetWidth)
      offsetRef.current += singleSetWidth;
    while (offsetRef.current > 0) offsetRef.current -= singleSetWidth;
    dragOffsetRef.current = 0;
    isDraggingRef.current = false;
    isPausedRef.current = false;
    const el = wrapperRef.current;
    if (el) el.style.cursor = 'grab';
  };

  useEffect(() => {
    window.addEventListener('pointermove', onPointerMoveWindow);
    window.addEventListener('pointerup', onPointerUpWindow);
    window.addEventListener('pointercancel', onPointerUpWindow);
    return () => {
      window.removeEventListener('pointermove', onPointerMoveWindow);
      window.removeEventListener('pointerup', onPointerUpWindow);
      window.removeEventListener('pointercancel', onPointerUpWindow);
    };
  }, []);

  const handleMouseEnter = () => (isPausedRef.current = true);
  const handleMouseLeave = () => {
    if (!isDraggingRef.current) isPausedRef.current = false;
  };

  const handleDotClick = (index: number) => {
    isPausedRef.current = true;

    const targetOffset = -index * VISIBLE_CARDS * CARD_TOTAL_WIDTH;
    const current = offsetRef.current;

    let start: number | null = null;
    const duration = 600; //animation duration in ms

    const easeInOutCubic = (t: number) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const smoothScroll = (timestamp: number) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeInOutCubic(progress);

      offsetRef.current = current + (targetOffset - current) * eased;
      applyTransform();

      if (progress < 1) {
        requestAnimationFrame(smoothScroll);
      } else {
        isPausedRef.current = false;
      }
    };

    requestAnimationFrame(smoothScroll);
  };

  if (slides.length === 0) {
    return <CarouselContainer />;
  }

  return (
    <CarouselContainer
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <SlidesWrapper
        ref={wrapperRef}
        $totalWidth={`${wrapperWidth}px`}
        $activeIndex={activeDot}
        onPointerDown={onPointerDown}
        style={{
          cursor: isStatic ? 'default' : 'grab',
          transition: isStatic ? 'none' : 'transform 0.5s ease-in-out',
        }}
      >
        {(isStatic ? slides : repeated).map((slide, i) => (
          <CarouselSlide key={i}>{slide}</CarouselSlide>
        ))}
      </SlidesWrapper>

      {!isStatic && (
        <DotsContainer>
          {Array.from({ length: totalDots }).map((_, i) => (
            <Dot
              key={i}
              $isActive={i === activeDot}
              onClick={() => handleDotClick(i)}
              style={{ cursor: 'pointer' }}
            />
          ))}
        </DotsContainer>
      )}
    </CarouselContainer>
  );
};

export default Carousel;
